import { html, TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";
import { type Label, type AccentColor, FilterElement } from "@/components/search/filter_element";
import { I18n } from "@/i18n/i18n";
import { FilterCollection } from "@/components/search/filter_collection";
import { generateUniqueId } from "@/utilities";

/**
 * This component inherits from FilterCollectionElement.
 * It represents a dropdown which allows to select one or multiple labels
 *
 * @element d-dropdown-filter
 *
 * @prop {AccentColor} color - The color associated with the filter
 * @prop {string} param - The searchQuery param to be used for this filter
 * @prop {boolean} multi - Whether one or more labels can be selected at the same time
 * @prop {[Label]} labels - All labels that could potentially be selected
 * @prop {string} filter - The current filter string (Internal)
 */
@customElement("d-dropdown-filter")
export class DropdownFilter extends FilterElement {
    @property({ type: String })
    color: AccentColor;

    @property({ state: true })
    filter = "";

    // Used to build unique ids per component instance (avoids duplicates on the same page)
    private readonly uniqueId = generateUniqueId();

    private readonly searchInputRef = createRef<HTMLInputElement>();

    get showFilter(): boolean {
        return this.labels.length > 15;
    }

    get filteredLabels(): Label[] {
        return this.showFilter
            ? this.labels.filter((s) => s.name.toLowerCase().includes(this.filter.toLowerCase()))
            : this.labels;
    }

    get disabled(): boolean {
        return this.labels.length === 0 || (!this.multi && this.labels.length === 1);
    }

    onFilterInput(e: Event): void {
        this.filter = (e.currentTarget as HTMLInputElement).value;
    }

    // Focus the search field when the dropdown opens, so you can filter by typing straight away.
    onDropdownShown(): void {
        this.searchInputRef.value?.focus();
    }

    onOptionClick(e: Event): void {
        const input = e.currentTarget as HTMLInputElement;
        const id = input.dataset.labelId;
        const label = this.labels.find((l) => String(l.id) === id);

        this.toggle(label);
    }

    render(): TemplateResult {
        const safeParam = (this.param || "filter").replace(/[^a-zA-Z0-9_-]/g, "_");
        const dropdownId = `dropdownMenu-${safeParam}-${this.uniqueId}`;
        const menuId = `${dropdownId}-menu`;
        const radioName = `radio-${safeParam}-${this.uniqueId}`;

        return html`
            <div class="dropdown dropdown-filter" @shown.bs.dropdown=${this.onDropdownShown}>
                <button
                    type="button"
                    class="token token-bordered ${this.disabled ? "disabled" : ""}"
                    id="${dropdownId}"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    aria-haspopup="true"
                    aria-controls="${menuId}"
                    aria-disabled="${this.disabled ? "true" : "false"}"
                    ?disabled=${this.disabled}
                >
                    ${this.getSelectedLabels().map(
                        () =>
                            html`<i
                                class="mdi mdi-circle mdi-12 mdi-colored-accent accent-${this.color} left-icon"
                                aria-hidden="true"
                            ></i>`,
                    )}
                    ${I18n.t(`js.search.filter.${this.param}`)}
                    <i class="mdi mdi-chevron-down mdi-18 right-icon" aria-hidden="true"></i>
                </button>

                <ul class="dropdown-menu" id="${menuId}" aria-labelledby="${dropdownId}">
                    ${
                        this.showFilter
                            ? html`
                                  <li>
                                      <span class="dropdown-item-text">
                                          <input
                                              type="text"
                                              class="form-control"
                                              ${ref(this.searchInputRef)}
                                              @input=${this.onFilterInput}
                                              placeholder="${I18n.t("js.dropdown.search")}"
                                              aria-label="${I18n.t("js.dropdown.search")} ${this.param}"
                                          />
                                      </span>
                                  </li>
                              `
                            : ""
                    }
                    ${this.filteredLabels
                        .sort((a, b) => b.count - a.count)
                        .map((s) => {
                            const optionId = `check-${safeParam}-${this.uniqueId}-${s.id}`;
                            return html`
                                <li>
                                    <span class="dropdown-item-text">
                                        <div class="form-check">
                                            <input
                                                class="form-check-input"
                                                type="${this.multi ? "checkbox" : "radio"}"
                                                name="${this.multi ? "" : radioName}"
                                                .checked=${this.isSelected(s)}
                                                data-label-id="${String(s.id)}"
                                                @click=${this.onOptionClick}
                                                id="${optionId}"
                                            />
                                            <label class="form-check-label" for="${optionId}">
                                                ${s.name}
                                                ${
                                                    s.count
                                                        ? html`<span class="text-muted float-end ms-4"
                                                              >${s.count}</span
                                                          >`
                                                        : ""
                                                }
                                            </label>
                                        </div>
                                    </span>
                                </li>
                            `;
                        })}
                </ul>
            </div>
        `;
    }
}

/**
 * This component represents a list of d-dropdown-filters and d-switch-filters
 *
 * @element d-search-filters
 *
 * @prop {FilterOptions[]} filters - the list of filterOptions for which a dropdown/switch should be displayed
 * @prop {string[]} hide - the list of filter params that should be hidden
 */
@customElement("d-search-filters")
export class SearchFilters extends FilterCollection {
    render(): TemplateResult {
        if (this.visibleDropdowns.length === 0 && this.visibleSwitches.length === 0) {
            return html``;
        }

        return html`
            ${this.visibleDropdowns.map(
                (c) => html`
                    <d-dropdown-filter .labels=${c.data} .color=${c.color} .param=${c.param} .multi=${c.multi}>
                    </d-dropdown-filter>
                `,
            )}
            ${this.visibleSwitches.map((c) =>
                c.triState
                    ? html`<d-tri-switch-filter .param=${c.param} .default=${c.default}></d-tri-switch-filter>`
                    : html`<d-switch-filter .param=${c.param} .default=${c.default}></d-switch-filter>`,
            )}
        `;
    }
}
